import graphql from "babel-plugin-relay/macro";
import { AnimalPublicTagSearchResultRow } from "components/animal/AnimalTagSearch/AnimalPublicTagSearchResultRow";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { animalMessages } from "domain/animal/animalMessages";
import { lostPetMessages } from "domain/lostPet/lostPetMessages";
import { AnimalPublicTagSearchResultQuery, RecaptchaVersion } from "generatedQueries/AnimalPublicTagSearchResultQuery.graphql";
import React, { FC, useEffect, useState } from "react";
import { Query } from "shared/components/Query";
import { WarningMessage } from "shared/components/WarningMessage";
import { sharedMessages } from "shared/sharedMessages";
import { useIntl } from "react-intl";
import { mapMessages } from "shared/mapMessages";
import { Message } from "shared/components/Message";

export const messages = mapMessages("components.animal.tagSearch", {
    searchingMessage: "Searching for pets...",
    resultsFoundMessage: "Search complete. {count} pets found.",
});

export interface IAnimalSearchProps {
    tag: string | null;
    recaptchaResponse: string | null;
    recaptchaVersion: RecaptchaVersion;
}

interface IProps {
    maxAnimalsToDisplay?: number;
    onResultRendering?: () => void;
    onSetStatus: (value: string) => void;
    onSetRecaptchaVersion: (value: RecaptchaVersion) => void;
}

type Props = IAnimalSearchProps & IProps;

export const AnimalPublicTagSearchResult: FC<Props> = ({
    maxAnimalsToDisplay,
    onResultRendering,
    tag,
    recaptchaResponse,
    recaptchaVersion,
    onSetStatus,
    onSetRecaptchaVersion,
}) => {
    const { formatMessage } = useIntl();
    const [statusMessage, setStatusMessage] = useState<string>("");
    const [newRecaptchaVersion, setNewRecaptchaVersion] = useState<RecaptchaVersion | undefined>(undefined);
    const [isComplete, setIsComplete] = useState<boolean>(false);

    useEffect(() => {
        onSetStatus(statusMessage);
    }, [statusMessage, onSetStatus]);

    useEffect(() => {
        if (newRecaptchaVersion){
            onSetRecaptchaVersion(newRecaptchaVersion);
        }
    }, [newRecaptchaVersion, onSetRecaptchaVersion]);

    useEffect(() => {
        if (isComplete && onResultRendering) {
            onResultRendering();
        }
    }, [isComplete, onResultRendering])

    if (!tag || !recaptchaResponse || !recaptchaVersion) {
        return null;
    }

    return (
        <Query<AnimalPublicTagSearchResultQuery>
            query={graphql`
                query AnimalPublicTagSearchResultQuery($tag: String!, $recaptchaResponse: String, $recaptchaVersion: RecaptchaVersion!) {
                    animalPublicTagSearch(tag: $tag, recaptcha: $recaptchaResponse, recaptchaVersion: $recaptchaVersion) {
                        recaptchaErrorCode
                        publicTagSearchResults {
                            ...AnimalPublicTagSearchResultRow_searchResult
                        }
                    }
                    configuration {
                        ...AnimalPublicTagSearchResultRow_configuration
                    }
                }`}
            variables={{
                recaptchaResponse,
                tag,
                recaptchaVersion,
            }}>
            {({ error, props }) => {
                setIsComplete(false);

                if (error) {
                    return renderResult(<ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />);
                } else if (props && props.animalPublicTagSearch && props.animalPublicTagSearch.recaptchaErrorCode) {
                    if (recaptchaVersion === "V3") {
                        setNewRecaptchaVersion("V2");
                    }
                    else {
                        return renderResult(<WarningMessage message={lostPetMessages.recapchaValidationMessage} />);
                    }
                } else if (props && (!props.animalPublicTagSearch?.publicTagSearchResults || !props.animalPublicTagSearch.publicTagSearchResults.length)) {
                    setStatusMessage(formatMessage(animalMessages.animalPublicTagNotFoundMessage));
                    return renderResult(<Message message={animalMessages.animalPublicTagNotFoundMessage} />);
                } else if (!props || !props.animalPublicTagSearch) {
                    setStatusMessage(formatMessage(messages.searchingMessage));
                    return null;
                }

                const {
                    animalPublicTagSearch,
                    configuration,
                } = props;

                if (!animalPublicTagSearch.publicTagSearchResults) {
                    return null;
                }

                const resultsToRender = maxAnimalsToDisplay !== undefined && maxAnimalsToDisplay > 0
                    ? animalPublicTagSearch.publicTagSearchResults.slice(0, maxAnimalsToDisplay)
                    : animalPublicTagSearch.publicTagSearchResults;

                setStatusMessage(formatMessage(messages.resultsFoundMessage, { count: resultsToRender.length }));
                return renderResult(<>
                    {resultsToRender.map((searchResult, index) => <AnimalPublicTagSearchResultRow key={index} searchResult={searchResult} configuration={configuration} />)}
                </>);
            }}
        </Query>
    );

    function renderResult(result: JSX.Element): JSX.Element {
        setIsComplete(true);
        return result;
    }
};
